import { Link } from "react-router-dom";
import { Phone, MessageCircle, Mail, MapPin, Clock, Facebook, Instagram } from "lucide-react";
import { Logo } from "@/components/Logo";
import { company } from "@/config/content";
import { useLang } from "@/i18n/LanguageContext";

export function Footer() {
  const { t } = useLang();
  const year = new Date().getFullYear();

  const contacts = [
    { icon: Phone, label: company.phoneDisplay, href: company.phoneHref, testId: "footer-phone" },
    { icon: MessageCircle, label: "WhatsApp", href: `https://wa.me/${company.whatsapp}`, testId: "footer-whatsapp" },
    { icon: Mail, label: company.email, href: `mailto:${company.email}`, testId: "footer-email" },
  ];

  return (
    <footer data-testid="site-footer" className="border-t border-edge bg-asphalt pb-24 pt-16 lg:pb-16">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-4">
          <div className="lg:col-span-1">
            <Logo />
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-steel">{t.footer.desc}</p>
            <div className="mt-5 flex gap-3">
              {company.social.facebook && company.social.facebook !== "#" && (
                <a href={company.social.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="flex h-9 w-9 items-center justify-center rounded-md border border-edge text-steel hover:border-amber hover:text-amber">
                  <Facebook className="h-4 w-4" />
                </a>
              )}
              {company.social.instagram && company.social.instagram !== "#" && (
                <a href={company.social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="flex h-9 w-9 items-center justify-center rounded-md border border-edge text-steel hover:border-amber hover:text-amber">
                  <Instagram className="h-4 w-4" />
                </a>
              )}
            </div>
          </div>

          <div>
            <h4 className="font-display text-sm font-bold uppercase tracking-wide text-ice">{t.footer.contacts}</h4>
            <ul className="mt-4 space-y-3">
              {contacts.map((c) => (
                <li key={c.testId}>
                  <a href={c.href} data-testid={c.testId} className="inline-flex items-center gap-2 text-sm text-steel transition-colors hover:text-amber">
                    <c.icon className="h-4 w-4 text-amber" /> {c.label}
                  </a>
                </li>
              ))}
              <li className="inline-flex items-start gap-2 text-sm text-steel"><MapPin className="mt-0.5 h-4 w-4 text-amber" /> {company.address}</li>
              <li className="inline-flex items-center gap-2 text-sm text-steel"><Clock className="h-4 w-4 text-amber" /> {company.hours}</li>
            </ul>
          </div>

          <div>
            <h4 className="font-display text-sm font-bold uppercase tracking-wide text-ice">{t.footer.pages}</h4>
            <ul className="mt-4 space-y-3 text-sm">
              <li><Link to="/" className="text-steel hover:text-amber">{t.nav.home}</Link></li>
              <li><Link to="/servizi" className="text-steel hover:text-amber">{t.nav.services}</Link></li>
              <li><Link to="/azienda" className="text-steel hover:text-amber">{t.nav.company}</Link></li>
              <li><Link to="/contatti" className="text-steel hover:text-amber">{t.nav.contact}</Link></li>
              <li><Link to="/privacy" className="text-steel hover:text-amber">{t.footer.privacy}</Link></li>
              <li><Link to="/cookie" className="text-steel hover:text-amber">{t.footer.cookie}</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-display text-sm font-bold uppercase tracking-wide text-ice">{t.footer.area}</h4>
            <p className="mt-4 text-sm leading-relaxed text-steel">{company.areaFull}</p>
            <p className="mt-4 text-xs text-steel">P. IVA: {company.vat}</p>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-3 border-t border-edge pt-6 text-xs text-steel sm:flex-row">
          <p>© {year} {company.name}. {t.footer.rights}</p>
          <p className="flex gap-4">
            <Link to="/privacy" className="hover:text-amber">{t.footer.privacy}</Link>
            <Link to="/cookie" className="hover:text-amber">{t.footer.cookie}</Link>
          </p>
        </div>
      </div>
    </footer>
  );
}
